import { useState, useEffect, useCallback } from "react";
import { FileText, Eye, Download, Trash2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { UploadItem } from "@/contexts/UploadContext";
import { extractOriginalFileName } from "@/lib/fileNameUtils";
import { FileRenameButton } from "@/components/ui/file-rename-button";
import { FileThumbnail } from "@/components/ui/file-thumbnail";
import { useDocumentDisplayName } from "@/hooks/useDocumentDisplayNames";
import { openFileInNewTab, extractBucketAndPath, getServeDocumentUrl } from "@/lib/documentMetadata";

interface FileListItemProps {
  fileName: string;
  url?: string; // can be relative storage path or full URL
  uploadItem?: UploadItem;
  onRemove?: () => void;
  onRetry?: () => void;
  onRenameSuccess?: (newUrl: string) => void;
  canView?: boolean;
  canDownload?: boolean;
  bucket?: 'partner-logos' | 'contract-documents';
  /** Show thumbnail preview for images */
  showThumbnail?: boolean;
}

export const FileListItem = ({
  fileName,
  url,
  uploadItem,
  onRemove,
  onRetry,
  onRenameSuccess,
  canView = true,
  canDownload = true,
  bucket,
  showThumbnail = true,
}: FileListItemProps) => {
  const status = uploadItem?.status || 'success';
  const progress = uploadItem?.progress || 100;
  
  // Resolve public URL for display name lookup
  const resolvePublicUrlForName = useCallback((rawUrl: string, bucketParam?: 'partner-logos' | 'contract-documents'): string => {
    if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
    const bucketName = bucketParam || uploadItem?.bucket || 'contract-documents';
    const { data } = supabase.storage.from(bucketName).getPublicUrl(rawUrl);
    return data?.publicUrl ?? rawUrl;
  }, [uploadItem?.bucket]);
  
  // Use React Query hook for display name - automatically updates when cache invalidates
  const resolvedUrl = url ? resolvePublicUrlForName(url, bucket) : null;
  const { displayName: hookDisplayName, isLoading } = useDocumentDisplayName(
    status === 'success' ? resolvedUrl : null
  );
  
  // Fallback to extracted name or provided fileName
  const extractedName = url ? extractOriginalFileName(url) : null;
  const safeDisplayFileName = hookDisplayName || extractedName || fileName || 'Dokument';
  
  // Determine file type for thumbnail
  const getFileType = () => {
    const lowerName = safeDisplayFileName.toLowerCase();
    if (lowerName.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/)) return 'image';
    if (lowerName.endsWith('.pdf')) return 'pdf';
    if (lowerName.match(/\.(xlsx?|csv)$/)) return 'excel';
    if (lowerName.match(/\.docx?$/)) return 'word';
    return 'file';
  };
  
  const getBgColor = () => {
    switch (status) {
      case 'uploading':
        return 'bg-chart-2/10 dark:bg-chart-2/20 border border-chart-2/30 dark:border-chart-2/40';
      case 'error':
        return 'bg-destructive/10 border border-destructive/30';
      default:
        return 'bg-muted';
    }
  };

  const resolvePublicUrl = (rawUrl: string, bucketParam?: 'partner-logos' | 'contract-documents'): string | null => {
    if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
    const bucketName = bucketParam || uploadItem?.bucket || 'contract-documents';
    const { data } = supabase.storage.from(bucketName).getPublicUrl(rawUrl);
    return data?.publicUrl ?? null;
  };

  const handleView = async () => {
    if (!url) return;
    const publicUrl = resolvePublicUrl(url, bucket);
    if (!publicUrl) return;
    
    // Use Edge Function proxy for correct filename in all download methods
    await openFileInNewTab(publicUrl, bucket || 'contract-documents');
  };

  const handleDownload = async () => {
    if (!url) return;
    const publicUrl = resolvePublicUrl(url, bucket);
    if (!publicUrl) return;
    
    // Use downloadFileWithCorrectName which includes Authorization header
    const { downloadFileWithCorrectName } = await import('@/lib/documentMetadata');
    await downloadFileWithCorrectName(publicUrl, bucket || 'contract-documents');
  };

  // Callback when file is renamed - cache is automatically invalidated by FileRenameButton
  const handleRenameSuccess = (newUrl: string) => {
    // Call parent callback - React Query will automatically refetch display name
    if (onRenameSuccess) {
      onRenameSuccess(newUrl);
    }
  };

  const renameUrl = url ? resolvePublicUrl(url, bucket) || url : undefined;

  const publicUrlForThumbnail = url ? resolvePublicUrl(url, bucket) : undefined;

  return (
    <div className={`flex flex-col gap-2 p-2 rounded text-sm ${getBgColor()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {status === 'error' ? (
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
          ) : showThumbnail && publicUrlForThumbnail ? (
            <FileThumbnail
              fileUrl={publicUrlForThumbnail}
              fileType={getFileType()}
              fileName={safeDisplayFileName}
              size="sm"
              className="flex-shrink-0"
            />
          ) : (
            <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          )}
          <span className="truncate font-medium">{safeDisplayFileName}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {status === 'success' && renameUrl && (
            <FileRenameButton
              currentUrl={renameUrl}
              onRenameSuccess={handleRenameSuccess}
            />
          )}
          {status === 'error' && onRetry && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={onRetry}
                  >
                    <RefreshCw className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Erneut versuchen</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {status === 'success' && canView && url && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleView}
            >
              <Eye className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
          {status === 'success' && canDownload && url && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleDownload}
            >
              <Download className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>
      
      {status === 'uploading' && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Wird hochgeladen...</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>
      )}
      
      {status === 'error' && uploadItem?.error && (
        <div className="text-xs text-destructive">
          Fehler: {uploadItem.error}
        </div>
      )}
    </div>
  );
};
